import React, { useCallback, useEffect, useState } from 'react'

import translate from '~/locale'

import { Container, Item, Message } from './styles'

export type Option = { name: string; value: string }

type AddressListProps = {
  open?: boolean
  loading?: boolean
  options: Array<Option>
  onSelect?: (option: Option) => void
}

const AddressList: React.FC<AddressListProps> = ({
  open,
  loading,
  options,
  onSelect,
}) => {
  const [focusedElement, setFocusedElement] = useState<number>(0)

  const handleClick = useCallback(
    (option: Option) => {
      if (onSelect) {
        onSelect(option)
      }
    },
    [onSelect],
  )

  useEffect(() => {
    if (options) {
      setFocusedElement(0)
    }
  }, [options])

  const handleKeydown = useCallback(
    (event) => {
      if (event.keyCode === 40) {
        setFocusedElement((current) =>
          Math.min(options.length - 1, current + 1),
        )
      } else if (event.keyCode === 38) {
        setFocusedElement((current) => Math.max(0, current - 1))
      } else if (event.keyCode === 13 && open && onSelect) {
        onSelect(options[focusedElement])
      }
    },
    [options, focusedElement, onSelect, open],
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeydown)

    return () => window.removeEventListener('keydown', handleKeydown)
  }, [handleKeydown])

  return open ? (
    <Container tabIndex={0}>
      {loading ? (
        <Message>{translate('autocomplete_loading')}</Message>
      ) : options.length ? (
        options.map((option, index) => (
          <Item
            key={option.value}
            tabIndex={-1}
            active={focusedElement === index}
            onClick={() => handleClick(option)}
          >
            {option.name}
          </Item>
        ))
      ) : (
        <Message>{translate('autocomplete_no_results')}</Message>
      )}
    </Container>
  ) : null
}

export default AddressList
